'use client';

import { Shield, BookOpen, BarChart3, Award } from 'lucide-react';
import { SectionHeader, PAGE_CONTAINER } from './ui';
import { useInView } from '@/hooks/useInView';
import { useTranslations } from 'next-intl';

const FEATURE_ICONS = [Shield, BookOpen, BarChart3, Award];
const FEATURE_STYLES = [
  { bg: 'bg-teal-50', iconBg: 'bg-teal-600', accent: 'text-teal-600', border: 'border-teal-100' },
  { bg: 'bg-orange-50', iconBg: 'bg-orange-500', accent: 'text-orange-600', border: 'border-orange-100' },
  { bg: 'bg-blue-50', iconBg: 'bg-blue-600', accent: 'text-blue-600', border: 'border-blue-100' },
  { bg: 'bg-emerald-50', iconBg: 'bg-emerald-600', accent: 'text-emerald-600', border: 'border-emerald-100' },
];

export function Features() {
  const t = useTranslations('home.features');
  const [sectionRef, inView] = useInView<HTMLElement>({ threshold: 0.1 });

  const items = (t.raw('items') as Array<{ title: string; description: string }>);

  return (
    <section ref={sectionRef} id="features" className="py-10 lg:py-16 bg-white">
      <div className={PAGE_CONTAINER}>
        <SectionHeader
          badge={t('badge')}
          badgeVariant="teal"
          title={t('title')}
          description={t('description')}
          className="mb-8 lg:mb-12"
        />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {items.map((item, index) => {
            const Icon = FEATURE_ICONS[index];
            const style = FEATURE_STYLES[index];
            return (
              <div
                key={index}
                style={{ transitionDelay: `${index * 100}ms` }}
                className={`${style.bg} border ${style.border} rounded-2xl p-5 lg:p-6 flex flex-col gap-4
                  transition-all duration-300
                  ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
              >
                {/* Icon */}
                <div className={`w-12 h-12 lg:w-14 lg:h-14 ${style.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
                </div>

                {/* Text */}
                <div>
                  <h4 className="font-bold text-slate-900 text-sm lg:text-base mb-1.5">
                    {item.title}
                  </h4>
                  <p className="text-sm leading-relaxed text-slate-600">
                    {item.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
